import * as React from "react";
import moment from 'moment'
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";
import { RepeatableSection } from "../components/RepeatableSection";
import { CurrencyModel } from "@/app/models/newDelegation/currencyModel";

import { BelgiumMinimumWageExpandableItemModel } from "@/app/models/configuration/minimumWage/belgiumMinimumWageConfigurationModel";
import { DriverQualificationsType } from "@/app/constants/driverQualificationsType";

import { getDriverQualificationTypeName } from "@/app/common/converters/driverQualificationToTextConverter";
import { getBelgiumDriverQualificationType } from "@/app/services/contract/driverQualificationTypeResolver";

type Props = {
    allowedCurrencies?: string[]
    state: {
        configuration: { currencies: CurrencyModel[] }
        expandableItems: BelgiumMinimumWageExpandableItemModel[]
    }
    setState: React.Dispatch<React.SetStateAction<{ expandableItems: BelgiumMinimumWageExpandableItemModel[] }>>
}

const QUALIFICATION_TYPES_BELGIUM = [
  DriverQualificationsType.NotSet,
  DriverQualificationsType.Guard,
  DriverQualificationsType.DriverDuringTraining,
  DriverQualificationsType.VhiclesBelow7,
  DriverQualificationsType.JouniorCourier,
  DriverQualificationsType.VehiclesAbove7Below15,
  DriverQualificationsType.SeniorCourier,
  DriverQualificationsType.VehiclesAbove15,
  DriverQualificationsType.BelgiumFirstGroup,
  DriverQualificationsType.BelgiumSecondGroup,
  DriverQualificationsType.BelgiumThirdGroup,
  DriverQualificationsType.BelgiumFourthGroup,
]

export const BelgiumMinimumWageExpandableItemSection = ({ allowedCurrencies, state, setState }: Props) => {
    const { t } = useLocalize()

    return (
        <RepeatableSection<BelgiumMinimumWageExpandableItemModel>
            title={t('configuration.minimumWages.minimalHourRateTitle')}
            newRow={() => ({
                configurationValueId: 0,
                rateValue: 0,
                dateFrom: moment(),
                driverYears: 0,
                rateCurrency: state.configuration.currencies[0],
                driverQualifications: DriverQualificationsType.Guard,
                isNew: true,
                isEdited: true,
            })}
            columns={[
                {
                    order: 1,
                    label: t('configuration-minimumWage.from-date'),
                    type: "date",
                    get: (r) => r.dateFrom,
                    set: (r, v) => ({ ...r, dateFrom: moment(v) }),
                },
                {
                    order: 3,
                    label: t('common.type'),
                    type: "select",
                    options: QUALIFICATION_TYPES_BELGIUM.map((value) => ({
                        value, label: getDriverQualificationTypeName(value, t),
                    })),
                    get: (r) => typeof r.driverQualifications === 'number' ? getBelgiumDriverQualificationType(r.driverQualifications) : r.driverQualifications,
                    set: (r, v: DriverQualificationsType) => ({ ...r, driverQualifications: v }),
                },
                {
                    order: 4,
                    label: t('common.amount'),
                    type: "price",
                    get: (r) => r.rateValue,
                    set: (r, v: number) => ({ ...r, rateValue: Number(v) }),
                },
                {
                    order: 5,
                    label: t("configuration.countries.currency"),
                    type: "select", //"currency",
                    options: state.configuration.currencies.filter(c => Array.isArray(allowedCurrencies) ? allowedCurrencies.includes(c.code) : true).map((c) => ({ label: `${c.code} - ${c.name}`, value: c.code })).sort((a, b) => a.label.localeCompare(b.label)),
                    get: (r) => r.rateCurrency.code,
                    set: (r, v) => {
                        const selected = state.configuration.currencies.find(c => c.code === v)
                        return ({ ...r, rateCurrency: selected })
                    }
                },
            ]}
            canAddRow={true}
            rows={state.expandableItems}
            setRows={(next) => {
                setState(prev => {
                    const current = prev.expandableItems;
                    const nextRows = typeof next === "function" ? (next as (p: typeof current) => typeof current)(current) : next;
                    return { ...prev, expandableItems: nextRows };
                });
            }}
            sortRows={(a, b) => {
                const da = moment(a.dateFrom)
                const db = moment(b.dateFrom)
                const ta = da.valueOf()
                const tb = db.valueOf()
                if(ta===tb) return 0
                return ta > tb ? -1 : 1
            }}
        />
    )
}
